import { onAuthStateChanged, signOut } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { auth } from "../../utils/firebase";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addUser, removeUser } from "../../store/slices/userSlice";
import { SUPPORTED_LANGUAGES } from "../../utils/constants";
import { toggleGptSearchView } from "../../store/slices/gptSlice";
import { changeLanguage } from "../../store/slices/configSlice";
import { RootState } from "../../store";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Search, Compass, Globe, ChevronDown, User } from "lucide-react";

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((store: RootState) => store.user);
  const showGptSearch = useSelector((store: RootState) => store.gpt.showGptSearch);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = () => {
    signOut(auth).catch(() => navigate("/error"));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const { uid, email, displayName, photoURL } = user;
        dispatch(addUser({ uid, email, displayName, photoURL }));
        if (window.location.pathname === "/") navigate("/browse");
      } else {
        dispatch(removeUser());
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  const handleGptSearchClick = () => {
    dispatch(toggleGptSearchView());
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 w-full px-6 md:px-12 py-3 z-50 flex flex-col md:flex-row justify-between items-center transition-all duration-300 ${isScrolled ? "bg-[#141414] shadow-xl" : "bg-transparent"
        }`}
    >
      <div className="flex items-center space-x-10 w-full md:w-auto justify-between md:justify-start">
        <h1
          className="text-red-600 text-3xl md:text-3xl font-black tracking-tighter cursor-pointer select-none transition-transform hover:scale-105"
          onClick={() => navigate("/browse")}
        >
          FLIXFUSION
        </h1>

        {user && !showGptSearch && (
          <div className="hidden lg:flex items-center space-x-5 text-sm font-medium text-gray-200">
            <span className="hover:text-gray-400 cursor-pointer transition-colors">Home</span>
            <span className="hover:text-gray-400 cursor-pointer transition-colors font-normal">TV Shows</span>
            <span className="hover:text-gray-400 cursor-pointer transition-colors font-normal">Movies</span>
            <span className="hover:text-gray-400 cursor-pointer transition-colors font-normal">New & Popular</span>
          </div>
        )}
      </div>

      {user && (
        <div className="flex items-center space-x-6 mt-4 md:mt-0">
          <div className="flex items-center bg-black/40 backdrop-blur-md rounded-full px-1 py-1 border border-white/10 ring-1 ring-white/5 shadow-inner">
            {showGptSearch && (
              <div className="flex items-center px-4 border-r border-white/10">
                <Globe size={14} className="text-zinc-500 mr-2" />
                <select
                  className="bg-transparent text-[11px] font-bold text-gray-200 outline-none cursor-pointer uppercase tracking-wider h-8"
                  onChange={(e) => dispatch(changeLanguage(e.target.value))}
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <option key={lang.identifier} value={lang.identifier} className="bg-zinc-900">
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              className={`flex items-center space-x-2 px-6 py-2.5 rounded-full text-xs font-black transition-all uppercase tracking-widest ${showGptSearch
                  ? "bg-red-600 text-white shadow-lg shadow-red-600/30"
                  : "text-gray-200 hover:bg-white/10"
                }`}
              onClick={handleGptSearchClick}
            >
              {showGptSearch ? <Compass size={14} /> : <Search size={14} />}
              <span>{showGptSearch ? "Explore" : "AI Movie Search"}</span>
            </button>
          </div>

          {/* User Profile Dropdown */}
          <div
            className="relative group flex items-center space-x-2 cursor-pointer py-2"
            onMouseEnter={() => setShowProfileDropdown(true)}
            onMouseLeave={() => setShowProfileDropdown(false)}
          >
            <img
              className="w-8 h-8 rounded-md object-cover ring-1 ring-white/20 group-hover:ring-white transition-all"
              src={user?.photoURL || "https://occ-0-6247-2164.1.nflxso.net/dnm/api/v6/K6hjPJd6cR6FpVELC5Pd6ovHRSk/AAAABdpkabKqQAIn_9No_Bbjtiy4qz4FTp_065m3D6-zQz689lByL2mreM8m1n9q768Y4f6_Sxp_f0m47H4A6bB8j97IuO6M2L4.png?r=e6e"}
              alt="user"
            />
            <ChevronDown size={14} className={`text-zinc-500 transition-transform duration-300 ${showProfileDropdown ? "rotate-180 text-white" : ""}`} />

            <AnimatePresence>
              {showProfileDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute top-full right-0 mt-2 w-48 bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl overflow-hidden z-[60]"
                >
                  <div className="p-4 border-b border-white/10 bg-white/5">
                    <p className="text-xs font-bold text-white truncate uppercase tracking-wider">{user?.displayName}</p>
                    <p className="text-[10px] text-zinc-500 truncate mt-1">Netflix Member</p>
                  </div>
                  <div className="p-2">
                    <button className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-all">
                      <User size={14} />
                      <span>Account</span>
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-500/10 rounded-md transition-all mt-1 border-t border-white/5 pt-3"
                    >
                      <LogOut size={14} />
                      <span>Sign Out of FlixFusion</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </motion.header>
  );
};

export default Header;
